import React, { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import {
  isPrinterSupported,
  getPrinterConfig,
  setPrinterConfig,
  isAutoPrintEnabled,
  setAutoPrintEnabled,
  listPairedPrinters,
  testPrint,
  parsePrinterCode,
  requestBtPermission,
  isBluetoothEnabled,
  getPrinterLang,
  setPrinterLang,
  guessPrinterLang,
  getLabelLayout,
  setLabelLayout,
  setAutoRegalLetter,
  getLabelSize,
  setLabelSize,
  isLabelFrameOn,
  setLabelFrameOn,
} from '../services/printerService';
import { hasCustomFeature } from '../services/customFeatures';
import { useTranslation } from '../hooks/useTranslation';

/**
 * 🖨️ Einstellungen für den Bluetooth-Etikettendrucker.
 * Diskret: nur erreichbar über die App-Einstellungen. Nutzer ohne Drucker
 * sehen hier lediglich einen kurzen Hinweis und werden nicht weiter behelligt.
 */
export default function PrinterSettings({ colors, onClose }) {
  const { t } = useTranslation();
  const dark = colors?.darkMode;
  // Native Plugin-Fehler kommen deutsch ("Bluetooth-Berechtigung verweigert/fehlt") → übersetzte Anleitung
  const errText = (e, fb) => { const m = e?.message || ''; return /berechtigung|permission/i.test(m) ? t('printer.permDenied') : (m || fb); };
  const c = {
    bg: dark ? '#23272f' : '#fff',
    text: dark ? '#fff' : '#23272f',
    sub: dark ? '#aaa' : '#666',
    border: dark ? '#444' : '#ddd',
    accent: '#1976d2',
    box: dark ? '#2d333b' : '#f5f5f5',
  };

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState(getPrinterConfig());
  const [autoPrint, setAutoPrint] = useState(isAutoPrintEnabled());
  const [lang, setLang] = useState(getPrinterLang());
  const chooseLang = (l) => { setPrinterLang(l); setLang(l); };
  const [layout, setLayout] = useState(getLabelLayout());
  const chooseLayout = (l) => { setLabelLayout(l); setLayout(l); if (l === 'nachname') setAutoRegalLetter(true); };
  const [labelSize, setLabelSizeState] = useState(getLabelSize());
  const chooseSize = (k) => { setLabelSize(k); setLabelSizeState(k); };
  const [frameOn, setFrameOnState] = useState(isLabelFrameOn());
  const toggleFrame = () => { const n = !frameOn; setLabelFrameOn(n); setFrameOnState(n); };
  const [testing, setTesting] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [btOn, setBtOn] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [justConnected, setJustConnected] = useState(false); // ✅ Erfolgs-Banner nach Verbinden
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const controlsRef = useRef(null);
  const scanHandledRef = useRef(false); // Einmal-Flag: verhindert Doppel-Handling + falschen „abgebrochen"-Fehler
  const mountedRef = useRef(true);

  const supported = isPrinterSupported();

  const loadDevices = async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      setBtOn(await isBluetoothEnabled());
      const list = await listPairedPrinters();
      setDevices(list);
      if (list.length === 0) {
        setInfo(t('printer.noPaired'));
      }
    } catch (e) {
      setError(errText(e, t('printer.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  // Auf Android direkt beim Öffnen die gekoppelten Geräte laden
  useEffect(() => {
    if (supported) loadDevices();
  }, []); // nur beim Öffnen

  const chooseDevice = (d) => {
    const config = { address: d.address, name: d.name || d.address };
    setPrinterConfig(config);
    setSelected(config);
    // Druckertyp aus dem Namen ableiten (PC45d → ZPL, Bixolon → wie bisher). Unklar → nichts ändern.
    const guess = guessPrinterLang(d.name);
    if (guess) chooseLang(guess);
    setError(null);
    setJustConnected(true);
    setInfo(t('printer.printerSet', { name: config.name }));
  };

  const grantPermission = async () => {
    setError(null);
    setInfo(null);
    const ok = await requestBtPermission();
    if (ok) {
      setInfo(t('printer.permGranted'));
      loadDevices();
    } else {
      setError(t('printer.permDenied'));
    }
  };

  // Setzt den Drucker aus einem QR-/Text-Inhalt (von Eingabe und Scan genutzt)
  const applyCode = (text) => {
    const parsed = parsePrinterCode(text);
    if (!parsed) {
      setError(t('printer.noMac'));
      return false;
    }
    const config = parsed.network
      ? { address: parsed.address, name: `${t('printer.networkName')} ${parsed.address.replace(/^tcp:\/\//i, '')}` }
      : { address: parsed.address, name: t('printer.defaultName') };
    setPrinterConfig(config);
    setSelected(config);
    if (parsed.network) chooseLang('zpl'); // Netzwerk-Etikettendrucker = Desktop/ZPL
    setCodeInput('');
    setError(null);
    setJustConnected(true);
    setInfo(t('printer.printerConnected', { address: parsed.address }));
    return true;
  };

  const addByCode = () => {
    setError(null);
    setInfo(null);
    applyCode(codeInput);
  };

  const stopScan = () => {
    try {
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
    } catch { /* ignore */ }
    // Kamera-Stream HART freigeben. Sonst bleibt die Kamera nach dem Scan belegt
    // (bis App-Neustart) und der abgebrochene Decode-Loop wirkt wie ein Fehler.
    try {
      const v = videoRef.current;
      if (v && v.srcObject) {
        v.srcObject.getTracks().forEach((tk) => tk.stop());
        v.srcObject = null;
      }
    } catch { /* ignore */ }
    codeReaderRef.current = null;
    if (mountedRef.current) setScanning(false);
  };

  const startScan = async () => {
    setError(null);
    setInfo(null);
    scanHandledRef.current = false;
    setScanning(true);
    setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        // ZXing-Hints: der Bixolon-Code ist sehr klein → „try harder" und gezielt
        // nach QR/DataMatrix suchen (mehr Rechenaufwand pro Frame, aber zuverlässiger).
        const hints = new Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX, BarcodeFormat.AZTEC,
        ]);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
        codeReaderRef.current = reader;

        // Rückkamera in hoher Auflösung + Dauer-Autofokus erzwingen — sonst ist
        // der kleine Code unscharf und wird nie erkannt.
        const constraints = {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' }],
          },
        };

        // Kontinuierlich scannen (jeder Frame), bis ein Code erkannt wird.
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result) => {
            // ⚠️ Der Callback feuert oft, BEVOR das await unten controlsRef gesetzt hat.
            // Darum ein Einmal-Flag statt controlsRef prüfen — sonst wird die Kamera
            // nie gestoppt (bleibt bis Neustart an) und der Erfolg löst einen Fehler aus.
            if (result && !scanHandledRef.current && mountedRef.current) {
              scanHandledRef.current = true;
              stopScan();
              applyCode(result.getText ? result.getText() : result.text);
            }
          },
        );
        controlsRef.current = controls;
        // Falls schon während des await erkannt wurde: die späten Controls jetzt hart stoppen.
        if (scanHandledRef.current) { try { controls.stop(); } catch { /* ignore */ } }
      } catch (err) {
        // Nur einen ECHTEN Fehler anzeigen — ein erfolgreicher Scan stoppt den Loop absichtlich.
        if (mountedRef.current && !scanHandledRef.current) {
          stopScan();
          setError(t('printer.scanAborted'));
        }
      }
    }, 200);
  };

  // Scanner beim Schließen/Unmount sauber stoppen
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopScan(); };
  }, []);

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    setAutoPrintEnabled(next);
  };

  const handleTest = async () => {
    if (!selected?.address) { setError(t('printer.selectFirst')); return; }
    setError(null);
    setInfo(null);
    setTesting(true);
    try {
      await testPrint(selected.address, { direkt: true });
      setInfo(t('printer.testSent'));
    } catch (e) {
      setError(errText(e, t('printer.testFailed')));
    } finally {
      setTesting(false);
    }
  };

  // Kamera-Overlay zum QR-Scannen (über dem Drucker-Dialog)
  if (scanning) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: '#000', zIndex: 4000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: '#fff', padding: '16px 16px 4px', fontSize: 15 }}>{t('printer.scanTitle')}</div>
        <div style={{ color: '#bbb', padding: '0 24px 12px', fontSize: 12, textAlign: 'center' }}>
          {t('printer.scanHint')}
        </div>
        <video
          ref={videoRef}
          style={{ width: '100%', maxWidth: 420, maxHeight: '60vh', objectFit: 'cover', borderRadius: 12 }}
          muted
          playsInline
        />
        <button
          onClick={stopScan}
          style={{
            marginTop: 20, padding: '12px 28px', borderRadius: 10, border: 'none',
            background: '#fff', color: '#000', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('printer.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        background: c.bg, color: c.text, borderRadius: 14, width: '100%', maxWidth: 440,
        maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${c.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: c.bg,
        }}>
          <strong style={{ fontSize: 16 }}>{t('printer.title')}</strong>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: c.text }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {!supported ? (
            <div style={{ color: c.sub, fontSize: 14, lineHeight: 1.5 }}>
              {t('printer.notAndroid')}
            </div>
          ) : (
            <>
              {/* ✅ Erfolgs-Banner direkt nach dem Verbinden — mit sofort erreichbarem Testdruck */}
              {justConnected && selected?.address && (
                <div style={{
                  background: dark ? '#14331f' : '#e8f5e9',
                  border: '1px solid #4CAF50', borderRadius: 10,
                  padding: '12px', marginBottom: 12,
                }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: dark ? '#7fd99b' : '#2e7d32', marginBottom: 4 }}>
                    {t('printer.connectedTitle')}
                  </div>
                  <div style={{ fontSize: 13, color: c.sub, marginBottom: 10, lineHeight: 1.4 }}>
                    {t('printer.connectedHint', { name: selected.name || selected.address })} <strong>0000</strong>.
                  </div>
                  <button
                    onClick={handleTest}
                    disabled={testing}
                    style={{
                      width: '100%', padding: '11px', borderRadius: 10, border: 'none',
                      background: '#4CAF50', color: '#fff', fontSize: 14, fontWeight: 700,
                      cursor: testing ? 'default' : 'pointer',
                    }}
                  >
                    {testing ? t('printer.printingTest') : t('printer.testNow')}
                  </button>
                </div>
              )}
              {!btOn && (
                <div style={{
                  background: '#b71c1c', color: '#fff', borderRadius: 10,
                  padding: '10px 12px', marginBottom: 12, fontSize: 14, fontWeight: 600,
                }}>
                  {t('printer.btOff')}
                </div>
              )}
              <p style={{ color: c.sub, fontSize: 13, marginTop: 0, lineHeight: 1.5 }}>
                {t('printer.intro')}
              </p>

              {/* Aktueller Drucker */}
              <div style={{ background: c.box, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: c.sub }}>{t('printer.current')}</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>
                  {selected?.name ? selected.name : t('printer.none')}
                </div>
              </div>

              {/* Druckertyp / Druckersprache */}
              <div style={{ background: c.box, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: c.sub, marginBottom: 6 }}>{t('printer.type')}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { k: 'cpcl', label: t('printer.typeMobile'), s: t('printer.typeStandard') },
                    { k: 'zpl', label: t('printer.typeDesktop'), s: t('printer.typeDesktopSub') },
                  ].map((o) => (
                    <button key={o.k} onClick={() => chooseLang(o.k)} style={{
                      flex: 1, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${lang === o.k ? '#4CAF50' : c.border}`,
                      background: lang === o.k ? (dark ? '#1e3a24' : '#e8f5e9') : c.bg, color: c.text,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{lang === o.k ? '✓ ' : ''}{o.label}</div>
                      <div style={{ fontSize: 11, color: c.sub }}>{o.s}</div>
                    </button>
                  ))}
                </div>
                {lang === 'zpl' && (
                  <div style={{ fontSize: 11, color: c.sub, marginTop: 6, lineHeight: 1.4 }}>
                    {t('printer.zplHint')}
                  </div>
                )}
                {lang === 'zpl' && (
                  <>
                    <div style={{ fontSize: 12, color: c.sub, margin: '10px 0 6px' }}>{t('printer.sizeTitle')}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        { k: '100x150', label: t('printer.size150') },
                        { k: '100x75', label: t('printer.size75') },
                        { k: 'endlos', label: t('printer.sizeEndless') },
                      ].map((o) => (
                        <button key={o.k} data-testid={'size-' + o.k} onClick={() => chooseSize(o.k)} style={{
                          flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer', textAlign: 'center', fontSize: 12, fontWeight: 600,
                          border: `2px solid ${labelSize === o.k ? '#4CAF50' : c.border}`,
                          background: labelSize === o.k ? (dark ? '#1e3a24' : '#e8f5e9') : c.bg, color: c.text,
                        }}>{labelSize === o.k ? '✓ ' : ''}{o.label}</button>
                      ))}
                    </div>
                    {labelSize === 'endlos' && (
                      <div style={{ fontSize: 11, color: c.sub, marginTop: 6, lineHeight: 1.4 }}>{t('printer.endlessHint')}</div>
                    )}
                    <label data-testid="label-frame" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={frameOn} onChange={toggleFrame} style={{ width: 16, height: 16 }} />
                      <span>⬛ {t('printer.frame')}</span>
                    </label>
                  </>
                )}
                {lang === 'zpl' && hasCustomFeature('nachnameSortierung') && (
                  <>
                    <div style={{ fontSize: 12, color: c.sub, margin: '10px 0 6px' }}>{t('printer.layout')}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        { k: 'hausnummer', label: t('printer.layoutHouse'), s: '12 · Jan de Vries' },
                        { k: 'nachname', label: t('printer.layoutSurname'), s: 'de Vries (V.) · 12' },
                      ].map((o) => (
                        <button key={o.k} onClick={() => chooseLayout(o.k)} style={{
                          flex: 1, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                          border: `2px solid ${layout === o.k ? '#4CAF50' : c.border}`,
                          background: layout === o.k ? (dark ? '#1e3a24' : '#e8f5e9') : c.bg, color: c.text,
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{layout === o.k ? '✓ ' : ''}{o.label}</div>
                          <div style={{ fontSize: 11, color: c.sub }}>{o.s}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Auto-Druck-Schalter */}
              <div
                onClick={toggleAutoPrint}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: c.box, borderRadius: 10, padding: '10px 12px', marginBottom: 12, cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 14 }}>{t('printer.autoPrint')}</span>
                <span style={{
                  width: 44, height: 26, borderRadius: 13, position: 'relative', flexShrink: 0,
                  background: autoPrint ? '#4CAF50' : (dark ? '#555' : '#ccc'), transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', top: 3, left: autoPrint ? 21 : 3,
                    width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
              </div>

              {/* Bluetooth-Berechtigung (falls noch nicht erteilt) */}
              <button onClick={grantPermission} style={{
                width: '100%', padding: '10px', borderRadius: 10, marginBottom: 12,
                border: `1px solid ${c.border}`, background: c.box, color: c.text,
                fontSize: 13, cursor: 'pointer',
              }}>
                {t('printer.grantPerm')}
              </button>

              {/* Geräteliste */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: c.sub }}>{t('printer.paired')}</span>
                <button onClick={loadDevices} disabled={loading} style={{
                  background: 'none', border: `1px solid ${c.border}`, color: c.accent,
                  borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                }}>
                  {loading ? '⏳' : t('printer.refresh')}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {devices.map((d) => {
                  const active = selected?.address === d.address;
                  return (
                    <button
                      key={d.address}
                      onClick={() => chooseDevice(d)}
                      style={{
                        textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${active ? '#4CAF50' : c.border}`,
                        background: active ? (dark ? '#1e3a24' : '#e8f5e9') : c.bg, color: c.text,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{active ? '✓ ' : ''}{d.name || t('printer.unnamed')}</div>
                      <div style={{ fontSize: 11, color: c.sub }}>{d.address}</div>
                    </button>
                  );
                })}
              </div>

              {/* Alternative: per QR-Code / MAC hinzufügen (Bixolon Easy Pairing) */}
              <div style={{ marginTop: 4, marginBottom: 12, paddingTop: 12, borderTop: `1px solid ${c.border}` }}>
                <div style={{ fontSize: 13, color: c.sub, marginBottom: 6 }}>
                  {t('printer.addByCode')}
                </div>
                {/* QR-Code direkt mit der Kamera scannen */}
                <button onClick={startScan} style={{
                  width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: c.accent,
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8,
                }}>
                  {t('printer.scanQr')}
                </button>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder={t('printer.manualPh')}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 10, fontSize: 13,
                      border: `1px solid ${c.border}`, background: dark ? '#1b1f27' : '#fff', color: c.text,
                      boxSizing: 'border-box', minWidth: 0,
                    }}
                  />
                  <button onClick={addByCode} disabled={!codeInput.trim()} style={{
                    padding: '10px 14px', borderRadius: 10, border: `1px solid ${c.border}`, background: c.box,
                    color: c.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {t('printer.set')}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: c.sub, marginTop: 6, lineHeight: 1.4 }}>
                  {t('printer.codeHint')} <strong>0000</strong>.
                </div>
              </div>

              <button
                onClick={handleTest}
                disabled={testing || !selected?.address}
                style={{
                  width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  background: selected?.address ? c.accent : (dark ? '#555' : '#ccc'),
                  color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: selected?.address ? 'pointer' : 'not-allowed',
                }}
              >
                {testing ? t('printer.printing') : t('printer.test')}
              </button>

              {info && <div style={{ marginTop: 10, fontSize: 13, color: '#4CAF50' }}>{info}</div>}
              {error && <div style={{ marginTop: 10, fontSize: 13, color: '#f44336' }}>⚠️ {error}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
